import React, { useState } from "react";
import { Grid, Typography, Divider, Paper, Toolbar, LinearProgress, GridSize } from "@material-ui/core";
import ImageViewerItem from "./ImageViewerItem";
import PointCloudImageViewerItem from "./PointCloudImageViewerItem";
import DownloadIconButton from "../../components/common/DownloadIconButton";
import { downloadCapturesAsZip } from "src/utils/DownloadUtil";
import { RequestInputData } from "src/model/RequestInputData";
import { useStyles } from "src/styles/ImageViewerStyles";
import { PCD_HARDWARE_SOURCE_LIST } from "src/containers/ImageViewer/constant";

type ImageViewerProps = {
    captureToDisplay: any,
    inputData: RequestInputData
}

/**
 * The Component is used to show the list of images of selected capture
 */
const ImageViewer: React.FunctionComponent<ImageViewerProps> = (props: ImageViewerProps) => {
    const styles = useStyles();
    const suffix = 'FILE'

    const imageStyle = {
        style: {
            width: "calc(100% - 20px)",
            height: "calc((100vw - 20px) * 0.25)",
            paddingBottom: 20
        },
        itemXs: 4 as GridSize
    };

    const[displayProgressBar, setDisplayProgressBar] = useState(false);
    const isValidImageUrl = (imageUrl: string) => {
        const lowerCaseImageUrl = imageUrl.toLowerCase();
        return (lowerCaseImageUrl.indexOf(".jpg") !== -1 ||
            lowerCaseImageUrl.indexOf(".png") !== -1 ||
            lowerCaseImageUrl.indexOf(".pcd") !== -1 ||
            lowerCaseImageUrl.indexOf(".jpeg") !== -1);
    };

    const sortByFilteredImageLocationKeys = (array1: string[], array2: string[]) => {
        let keyA = array1[0].split("#")[0];
        let keyB = array2[0].split("#")[0];

        // Compare the keys
        if (keyA < keyB) {
          return -1;
        } else if (keyA > keyB) {
          return 1;
        } else {
          // If keys are equal, maintain the original order
          return array1[0].localeCompare(array2[0]);
        }
    };

    const updateKeys = (key: string, numOfFilesPerFileTypeMap: Map<string, number>) => {
        let sensorType = key.split("#")[0];
        if (numOfFilesPerFileTypeMap.get(sensorType) == 1)
            return `${sensorType}`
        let fileNum = (sensorType === key)? 1 : parseInt(key.split("#")[1]) + 1;
        return `${sensorType} ${suffix} ${fileNum}`
    }

    let displayImages;
    if(props.captureToDisplay) {
        const binaryFileLocations: object = props.captureToDisplay.binaryFileLocations;
        const filteredImageLocation = Object.entries(binaryFileLocations).filter(([key, value]) => isValidImageUrl(value));
        const displayPointCloudImages = (key: string, value: string) => {
            if (value.indexOf(".pcd") !== -1 && PCD_HARDWARE_SOURCE_LIST.includes(props.captureToDisplay.hardwareSource))
                return (<PointCloudImageViewerItem key={key}
                                                   hardwareSource={props.captureToDisplay.hardwareSource}
                                                   sensor={key}
                                                   imgUrl={value}/>);
            else
                return (<></>);
        };
        if (filteredImageLocation.length > 0) {
            const numOfFilesPerFileTypeMap = new Map<string, number>();
            filteredImageLocation.map(([key, value]) => {
                const fileType = key.split("#")[0];
                numOfFilesPerFileTypeMap.set(fileType, (numOfFilesPerFileTypeMap.get(fileType) || 0) + 1);
            });
            displayImages = (
                <Grid container spacing={3}>
                    {filteredImageLocation.sort(sortByFilteredImageLocationKeys).map(([key, value]) => {
                        key = updateKeys(key, numOfFilesPerFileTypeMap);
                        return value.indexOf(".pcd") !== -1
                            ? displayPointCloudImages(key, value)
                            : <ImageViewerItem key={key} imgName={key} imgUrl={value} imgStyle={imageStyle} />
                    })}
                </Grid>
            );
        } else {
            displayImages = (
                <Typography className={styles.imageViewerMessage} component="div">
                    The file type for selected sensor cannot be displayed! You can download selected capture to view the included file.
                </Typography>
            );
        }
    } else {
        displayImages = (
            <Typography className={styles.imageViewerMessage} component="div">
                Please select a capture in the table to view included images
            </Typography>
        );
    }

    const handleDownloadCapture = async () => {
        setDisplayProgressBar(true);
        await downloadCapturesAsZip([props.captureToDisplay], props.inputData);
        setDisplayProgressBar(false);
    };

    return(
        <Paper className={styles.paper}>
            <Grid className={styles.imgViewerContainer} container spacing={1}>
                <Grid item xs={12}>
                    <Toolbar variant="dense">
                        <Typography variant="subtitle1" className={styles.title} component="div">Image Viewer</Typography>
                        <div className={styles.flow}></div>
                        <DownloadIconButton
                            buttonTooltipText = "Download Selected Capture (.zip)"
                            disabled={!(props.captureToDisplay)}
                            onDownload = {handleDownloadCapture}/>
                    </Toolbar>
                    {displayProgressBar && <LinearProgress className={styles.progressBar}/>}
                    <Divider></Divider>
                </Grid>
                <Grid item xs={12}>
                    {displayImages}
                </Grid>
            </Grid>
        </Paper>
    );
}

export default ImageViewer;
